"use client";
import type {
  EmblaCarouselType,
  EmblaEventListType,
  EmblaEventModelType,
  EmblaOptionsType,
} from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import React, { useCallback, useEffect, useRef } from "react";


type Props = {
  options?: EmblaOptionsType;
  children: React.ReactNode;
  style?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
  enableMouseEvents?: boolean;
  resistance?: boolean;
  onIndexChange?: (index: number) => void;
};

export default function SwipeableViews(props: Props) {
  const { options, children, style, containerStyle, onIndexChange } = props;
  const defaultOptions: EmblaOptionsType = {
    loop: false,
    // Prevent fast flicks from skipping multiple slides
    skipSnaps: false,
    // Require a larger initial movement before Embla takes control
    dragThreshold: 20,
    axis: "x",
  };

  const mergedOptions: EmblaOptionsType = {
    ...(defaultOptions as EmblaOptionsType),
    ...(options || {}),
  };

  const [emblaRef, emblaApi, emblaServerApi] = useEmblaCarousel(mergedOptions);
  const slides = React.Children.toArray(children);

  const TWEEN_FACTOR_BASE = 0.84;
  const tweenFactor = useRef(0);

  const numberWithinRange = useCallback(
    (number: number, min: number, max: number) =>
      Math.min(Math.max(number, min), max),
    [],
  );

  const setTweenFactor = useCallback((api: EmblaCarouselType) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * api.snapList().length;
  }, []);

  const prevUserSelect = useRef<string | null>(null);

  const disableTextSelection = useCallback(() => {
    try {
      const body = document?.body;
      if (!body) return;
      prevUserSelect.current = body.style.userSelect ?? null;
      body.style.userSelect = "none";
    } catch (_) {}
  }, []);

  const enableTextSelection = useCallback(() => {
    try {
      const body = document?.body;
      if (!body) return;
      body.style.userSelect = prevUserSelect.current ?? "";
      prevUserSelect.current = null;
    } catch (_) {}
  }, []);

  const onPointerDownEdgeGuard = useCallback(
    (api: EmblaCarouselType, event: any) => {
      try {
        const edgeThreshold = 30; // Pixels from the edge to ignore
        const viewportWidth =
          typeof window !== "undefined" ? window.innerWidth : 0;

        // Embla may wrap the native event in `event.sourceEvent` or in `event.detail` — use those when available
        const nativeEvent = event?.sourceEvent ?? event?.detail ?? event;

        // Get the X coordinate from touch, pointer or mouse events in a robust way
        let touchX = 0;
        if (nativeEvent?.touches && nativeEvent.touches.length) {
          touchX = nativeEvent.touches[0].clientX;
        } else if (
          nativeEvent?.changedTouches &&
          nativeEvent.changedTouches.length
        ) {
          touchX = nativeEvent.changedTouches[0].clientX;
        } else if (typeof nativeEvent?.clientX === "number") {
          touchX = nativeEvent.clientX;
        }

        // Only cancel Embla when the gesture starts very close to the viewport edges
        if (
          touchX > 0 &&
          (touchX < edgeThreshold || touchX > viewportWidth - edgeThreshold)
        ) {
          // returning false tells Embla to ignore the drag so native edge gestures can work
          return false;
        }
        // Otherwise do nothing (undefined) so Embla proceeds normally
      } catch (_) {
        // Fail silently — don't block embla if something unexpected happens
      }
    },
    [],
  );

  const tweenOpacity = useCallback(
    <EventType extends keyof EmblaEventListType>(
      api: EmblaCarouselType,
      event?: EmblaEventModelType<EventType>,
    ) => {
      const engine = api.internalEngine();
      const scrollProgress = api.scrollProgress();
      const slidesInView = api.slidesInView();
      const isScrollEvent = event?.type === "scroll";

      api.snapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.scrollSnapList.slidesBySnap[snapIndex];

        slidesInSnap.forEach((slideIndex: number) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress);
                }
                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress);
                }
              }
            });
          }

          const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current);
          const opacity = numberWithinRange(tweenValue, 0, 1).toString();
          const node = api.slideNodes()[slideIndex] as HTMLElement | undefined;
          if (node) node.style.opacity = opacity;
        });
      });
    },
    [numberWithinRange],
  );

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      const selected = emblaApi.selectedSnap();
      onIndexChange?.(selected);
    };

    setTweenFactor(emblaApi);
    tweenOpacity(emblaApi);

    emblaApi
      .on("reinit", setTweenFactor)
      .on("reinit", tweenOpacity)
      .on("scroll", tweenOpacity)
      .on("slidefocus", tweenOpacity)
      .on("select", onSelect)
      .on("pointerdown", onPointerDownEdgeGuard)
      .on("pointerdown", disableTextSelection)
      .on("pointerup", enableTextSelection)
      .on("settle", enableTextSelection);

    return () => {
      emblaApi.off("reinit", setTweenFactor);
      emblaApi.off("reinit", tweenOpacity);
      emblaApi.off("scroll", tweenOpacity);
      emblaApi.off("slidefocus", tweenOpacity);
      emblaApi.off("select", onSelect);
      emblaApi.off("pointerdown", onPointerDownEdgeGuard);
      emblaApi.off("pointerdown", disableTextSelection);
      emblaApi.off("pointerup", enableTextSelection);
      emblaApi.off("settle", enableTextSelection);
    };
  }, [
    emblaApi,
    setTweenFactor,
    tweenOpacity,
    disableTextSelection,
    enableTextSelection,
    onIndexChange,
    onPointerDownEdgeGuard,
  ]);

  const renderSsrStyles = !emblaApi;

  return (
    <>
      {renderSsrStyles && (
        <style>
          {emblaServerApi.ssrStyles(".embla__container", ".embla__slide")}
        </style>
      )}
      <div className="embla" style={style}>
        <div
          className="embla__viewport"
          ref={emblaRef}
          style={{ overflow: "hidden" }}
        >
          <div
            className="embla__container"
            style={{
              display: "flex",
              width: "100%",
              ...(containerStyle || {}),
            }}
          >
            {slides.map((child, i) => (
              <div
                className="embla__slide"
                key={i}
                style={{ flex: "0 0 100%" }}
              >
                {child}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
